import { useEffect, useRef, useState } from "react";
import { Dialog, btnPrimary, btnSecondary } from "../../../components/ui";
import { downloadText, parseCsv, readFileText, toCsv } from "../../../lib/csv";
import { TOOLTIPS } from "../../../lib/tooltips";

/**
 * The "⋮" beside each Flow configuration tab (the owner's ask of
 * 2026-09-18, three vertical dots after the first walk): export the tab as CSV, import one - repaired from what
 * Excel does to it, with the repairs listed before anything is applied -
 * an example file so the shape is never a guess, and delete everything in
 * the tab at once instead of one by one.
 */
export interface CsvShape<T> {
  /** File stem: `announcements`, `kv-pairs`, `holidays`. */
  name: string;
  headers: string[];
  /** Headers a file may leave out - a column with a default, so an older export still imports. */
  optional?: string[];
  example: T[];
  toRow: (item: T) => Record<string, string>;
  /** Returns the item, or a sentence saying why the row cannot be one. */
  fromRow: (row: Record<string, string>, index: number) => T | string;
}

export function TabMenu<T>({ shape, items, onImport, onDeleteAll, what, canImport = true, canDeleteAll = true }: { shape: CsvShape<T>; items: T[]; onImport: (items: T[], replace: boolean) => void; onDeleteAll: () => void; what: string; /** ADR-0017: the create and delete verbs on this kind of thing; export never needs one. */ canImport?: boolean; canDeleteAll?: boolean }) {
  const [open, setOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);
  const item = "block w-full px-3 py-2 text-left text-sm hover:bg-neutral-100 disabled:opacity-50 dark:hover:bg-neutral-800";

  return (
    <div className="relative" ref={ref}>
      <button type="button" className={`${btnSecondary} min-h-8 px-2.5`} aria-haspopup="menu" aria-expanded={open} aria-label={`${what} actions`} title={TOOLTIPS["flow.tab-menu"]} onClick={() => setOpen((o) => !o)}>
        <span aria-hidden="true" className="text-base leading-none">⋮</span>
      </button>
      {open && (
        <div role="menu" className="absolute right-0 z-20 mt-1 w-60 rounded-lg border border-neutral-200 bg-white py-1 shadow-lg dark:border-neutral-700 dark:bg-neutral-900">
          <button role="menuitem" type="button" className={item} disabled={items.length === 0} onClick={() => { downloadText(`${shape.name}.csv`, toCsv(shape.headers, items.map(shape.toRow))); setOpen(false); }}>
            Export CSV ({items.length})
          </button>
          <button role="menuitem" type="button" className={item} disabled={!canImport} onClick={() => { setImporting(true); setOpen(false); }}>
            Import CSV…
          </button>
          <button role="menuitem" type="button" className={item} onClick={() => { downloadText(`${shape.name}-example.csv`, toCsv(shape.headers, shape.example.map(shape.toRow))); setOpen(false); }}>
            Example CSV
          </button>
          <button role="menuitem" type="button" className={`${item} text-red-700 dark:text-red-300`} disabled={items.length === 0 || !canDeleteAll} onClick={() => { setConfirmDelete(true); setOpen(false); }}>
            Delete all {what.toLowerCase()}…
          </button>
        </div>
      )}
      {importing && <ImportDialog shape={shape} what={what} existing={items.length} onClose={() => setImporting(false)} onImport={(list, replace) => { onImport(list, replace); setImporting(false); }} />}
      {confirmDelete && (
        <Dialog title={`Delete all ${items.length} ${what.toLowerCase()}?`} onClose={() => setConfirmDelete(false)}>
          <p className="text-sm">They are removed from this environment as soon as you confirm; the account pulls the change within five minutes. History keeps every earlier version to roll back to.</p>
          <div className="mt-4 flex gap-2">
            <button type="button" className={btnPrimary} onClick={() => { onDeleteAll(); setConfirmDelete(false); }}>Delete all</button>
            <button type="button" className={btnSecondary} onClick={() => setConfirmDelete(false)}>Cancel</button>
          </div>
        </Dialog>
      )}
    </div>
  );
}

function ImportDialog<T>({ shape, what, existing, onClose, onImport }: { shape: CsvShape<T>; what: string; existing: number; onClose: () => void; onImport: (items: T[], replace: boolean) => void }) {
  const [parsed, setParsed] = useState<{ items: T[]; problems: string[]; fixes: string[] }>();
  const [replace, setReplace] = useState(false);
  const [error, setError] = useState<string>();
  const read = async (file: File | undefined) => {
    setError(undefined);
    setParsed(undefined);
    if (!file) return;
    const out = parseCsv(await readFileText(file));
    const missing = shape.headers.filter((h) => !out.headers.includes(h) && !shape.optional?.includes(h));
    if (missing.length) {
      setError(`The file needs the columns ${shape.headers.filter((h) => !shape.optional?.includes(h)).join(", ")}; it is missing ${missing.join(", ")}. Download the example to see the shape.`);
      return;
    }
    const items: T[] = [];
    const problems: string[] = [];
    out.rows.forEach((row, i) => {
      const r = shape.fromRow(row, i + 2);
      if (typeof r === "string") problems.push(r);
      else items.push(r);
    });
    setParsed({ items, problems, fixes: out.fixes });
  };
  const example = `${shape.headers.join(",")}\n${shape.example.slice(0, 2).map((e) => shape.headers.map((h) => shape.toRow(e)[h] ?? "").join(",")).join("\n")}`;

  return (
    <Dialog title={`Import ${what.toLowerCase()} from CSV`} onClose={onClose} wide>
      <p className="text-sm text-neutral-600 dark:text-neutral-400">
        The file's first row names the columns: <code className="text-xs">{shape.headers.join(", ")}</code>. A file Excel has saved is repaired on the way in - the repairs are listed before anything changes.
      </p>
      <pre className="mt-2 overflow-x-auto rounded bg-neutral-100 p-2 text-xs dark:bg-neutral-800">{example}</pre>
      <input type="file" accept=".csv,text/csv" className="mt-3 block text-sm" aria-label="CSV file" onChange={(e) => void read(e.target.files?.[0])} />
      {error && <p role="alert" className="mt-2 text-sm text-red-700 dark:text-red-300">{error}</p>}
      {parsed && (
        <div className="mt-3 space-y-2 text-sm">
          <p><strong>{parsed.items.length}</strong> row{parsed.items.length === 1 ? "" : "s"} ready{parsed.problems.length ? `, ${parsed.problems.length} skipped` : ""}.</p>
          {parsed.fixes.length > 0 && (
            <div>
              <p className="font-medium">Repaired on the way in</p>
              <ul className="list-disc pl-5 text-xs text-neutral-600 dark:text-neutral-400">{parsed.fixes.map((f) => <li key={f}>{f}</li>)}</ul>
            </div>
          )}
          {parsed.problems.length > 0 && (
            <div role="alert">
              <p className="font-medium text-amber-800 dark:text-amber-200">Skipped</p>
              <ul className="list-disc pl-5 text-xs text-amber-800 dark:text-amber-200">{parsed.problems.map((p) => <li key={p}>{p}</li>)}</ul>
            </div>
          )}
          {existing > 0 && (
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={replace} onChange={(e) => setReplace(e.target.checked)} />
              Replace the {existing} already here (otherwise rows with the same name update them and the rest are added)
            </label>
          )}
        </div>
      )}
      <div className="mt-4 flex gap-2">
        <button type="button" className={btnPrimary} disabled={!parsed || parsed.items.length === 0} onClick={() => parsed && onImport(parsed.items, replace)}>
          Add
        </button>
        <button type="button" className={btnSecondary} onClick={onClose}>Cancel</button>
      </div>
    </Dialog>
  );
}
